import type { FaqContent } from '~/content/types';
import { DEFAULT_LANGUAGE } from '~/lib/language';
import { useContent, useLanguage } from '~/lib/useLanguage';
import { useSiteAvailability } from '~/lib/useSiteAvailability';

/**
 * Words that mark an FAQ entry as stablecoin-specific, so it can be hidden while
 * the feature is unavailable. Arabic terms are listed alongside the English ones
 * because the pattern runs against whichever language the page is rendering.
 */
const STABLECOIN_FAQ_PATTERN =
  /\b(?:stablecoin|cryptocurrency|crypto)\b|عملة مستقرة|العملات المستقرة|كريبتو/iu;

/**
 * The shared FAQ as a page should render it. Payload serves the FAQ in English
 * only, so a translated page uses the FAQ from its own content bundle instead of
 * the loader's, and stablecoin entries are dropped while that feature is off.
 */
export function useVisibleFaq(faqContent: FaqContent): FaqContent {
  const { stablecoin: stablecoinAvailable } = useSiteAvailability();
  const language = useLanguage();
  const { shared } = useContent();

  const localizedFaq = language === DEFAULT_LANGUAGE ? faqContent : shared.defaultFaq;
  if (stablecoinAvailable) return localizedFaq;
  return {
    ...localizedFaq,
    items: localizedFaq.items.filter(
      ({ question, answer }) => !STABLECOIN_FAQ_PATTERN.test(`${question} ${answer}`),
    ),
  };
}
